/**
 * MP4 videos for damage reports (stage 2 of docs/plans/2026-10-12-medien.md):
 * what a device must send and what the API accepts, in one place — like
 * avatar.ts for profile pictures and the JPEG rules for photos (rule 21).
 *
 * The danger is the place: an iPhone video carries where it was filmed in its
 * container (`©xyz`, `com.apple.quicktime.location.ISO6709` in `udta`/`meta`),
 * and the compressor the app uses keeps all metadata on purpose. So the
 * device neutralises every metadata box after compressing (`stripMp4Metadata`)
 * and the API refuses a video that still carries one (`inspectVideo`). Sound
 * stays (owner, 13.10.2026: „Ton muss im Video möglich sein … den Schaden noch
 * per Sprache beschreiben“) — only the container's metadata goes.
 *
 * Neutralising, not removing: the box keeps its size and becomes `free` with a
 * zeroed body, so no chunk offset (`stco`/`co64`) moves and the file still
 * plays. Pure; no DOM, no Node.
 */

/** Longest video, in seconds (owner, 12.10.2026). */
export const VIDEO_MAX_SECONDS = 30;
/** Videos per damage, besides the photos (owner, 12.10.2026). */
export const VIDEO_MAX_PER_DAMAGE = 2;
/** Long edge after compressing on the device: 720p, enough for a phone screen. */
export const VIDEO_MAX_EDGE = 1280;
/** Upper bound after compressing; 30 s of 720p is well below it. */
export const VIDEO_MAX_BYTES = 25 * 1024 * 1024;

/** Boxes that hold user metadata. Anything inside them may name a place. */
const METADATA_BOXES = new Set(['udta', 'meta', 'uuid', 'XMP_']);
/** Containers whose children are walked. */
const CONTAINERS = new Set(['moov', 'trak', 'mdia', 'minf', 'stbl', 'edts', 'dinf', 'moof', 'traf', 'mvex']);

/** Whether a track handler says sound (`hdlr` … `soun`). */
function isSoundHandler(bytes: Uint8Array, box: Box): boolean {
  // hdlr: version/flags (4), pre_defined (4), handler_type (4)
  const at = box.start + box.header + 8;
  return at + 4 <= box.start + box.size && String.fromCharCode(bytes[at]!, bytes[at + 1]!, bytes[at + 2]!, bytes[at + 3]!) === 'soun';
}
/** Atoms that name a place, wherever they appear (also as text inside `meta` keys). */
const LOCATION_MARKERS = ['©xyz', 'com.apple.quicktime.location', 'loci'];

interface Box {
  readonly type: string;
  readonly start: number;
  readonly size: number;
  readonly header: number;
}

function readBox(bytes: Uint8Array, at: number, end: number): Box | null {
  if (at + 8 > end) return null;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let size = view.getUint32(at);
  const type = String.fromCharCode(bytes[at + 4]!, bytes[at + 5]!, bytes[at + 6]!, bytes[at + 7]!);
  let header = 8;
  if (size === 1) {
    if (at + 16 > end) return null;
    const high = view.getUint32(at + 8);
    const low = view.getUint32(at + 12);
    size = high * 2 ** 32 + low;
    header = 16;
  } else if (size === 0) {
    size = end - at;
  }
  if (size < header || at + size > end) return null;
  return { type, start: at, size, header };
}

function* walk(bytes: Uint8Array, from: number, end: number, depth = 0): Generator<Box & { depth: number }> {
  let at = from;
  while (at < end) {
    const box = readBox(bytes, at, end);
    if (!box) return;
    yield { ...box, depth };
    if (CONTAINERS.has(box.type) && depth < 8) yield* walk(bytes, box.start + box.header, box.start + box.size, depth + 1);
    at = box.start + box.size;
  }
}

/** Turns every metadata box into a zeroed `free` box of the same size. Returns a new array. */
export function stripMp4Metadata(input: Uint8Array): Uint8Array {
  const bytes = new Uint8Array(input);
  for (const box of walk(bytes, 0, bytes.length)) {
    if (!METADATA_BOXES.has(box.type)) continue;
    // Top-level `meta` or `uuid` boxes outside moov are metadata too (HEIF-style, XMP).
    bytes.set([0x66, 0x72, 0x65, 0x65], box.start + 4); // 'free'
    bytes.fill(0, box.start + box.header, box.start + box.size);
  }
  return bytes;
}

export type VideoRefusal = 'not_mp4' | 'too_large' | 'too_long' | 'location';

export type VideoVerdict =
  | { readonly ok: true; readonly seconds: number; readonly hasSound: boolean }
  | { readonly ok: false; readonly reason: VideoRefusal };

/** Duration from `mvhd` (version 0 or 1), in seconds; null without one. */
export function mp4Duration(bytes: Uint8Array): number | null {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  for (const box of walk(bytes, 0, bytes.length)) {
    if (box.type !== 'mvhd') continue;
    const at = box.start + box.header;
    const version = bytes[at];
    // A truncated mvhd must not read past its box (a RangeError became a 500
    // instead of not_mp4; bug hunt 15.10.2026): v1 needs 32 bytes, v0 20.
    if (box.start + box.size - at < (version === 1 ? 32 : 20)) return null;
    if (version === 1) {
      const scale = view.getUint32(at + 20);
      const duration = view.getUint32(at + 24) * 2 ** 32 + view.getUint32(at + 28);
      return scale ? duration / scale : null;
    }
    const scale = view.getUint32(at + 12);
    const duration = view.getUint32(at + 16);
    return scale ? duration / scale : null;
  }
  return null;
}

function containsAscii(bytes: Uint8Array, text: string, from: number, to: number): boolean {
  const needle = Array.from(text, (c) => c.charCodeAt(0) & 0xff);
  outer: for (let i = from; i + needle.length <= to; i++) {
    for (let j = 0; j < needle.length; j++) if (bytes[i + j] !== needle[j]) continue outer;
    return true;
  }
  return false;
}

/**
 * The API's rule for a video upload: an MP4 (`ftyp` first), at most
 * VIDEO_MAX_BYTES, at most VIDEO_MAX_SECONDS (+ 1 s for rounding), and no
 * metadata box that is still alive or any place marker outside the media
 * data. `mdat` is skipped: compressed frames contain any byte sequence.
 */
export function inspectVideo(bytes: Uint8Array): VideoVerdict {
  const first = readBox(bytes, 0, bytes.length);
  if (!first || first.type !== 'ftyp') return { ok: false, reason: 'not_mp4' };
  if (bytes.length > VIDEO_MAX_BYTES) return { ok: false, reason: 'too_large' };
  let hasMoov = false;
  for (const box of walk(bytes, 0, bytes.length)) {
    if (box.type === 'moov') hasMoov = true;
    if (METADATA_BOXES.has(box.type)) return { ok: false, reason: 'location' };
    if (box.type === 'mdat' || box.type === 'free') continue;
    if (box.depth === 0 && box.type !== 'moov' && box.type !== 'ftyp' && box.type !== 'moof' && box.type !== 'mfra' && box.type !== 'wide' && box.type !== 'skip') continue;
  }
  if (!hasMoov) return { ok: false, reason: 'not_mp4' };
  // Place markers anywhere outside mdat (a writer might put them where we do not walk).
  for (const box of walk(bytes, 0, bytes.length)) {
    if (box.depth !== 0 || box.type === 'mdat' || box.type === 'free') continue;
    for (const marker of LOCATION_MARKERS) {
      if (containsAscii(bytes, marker, box.start + box.header, box.start + box.size)) return { ok: false, reason: 'location' };
    }
  }
  const seconds = mp4Duration(bytes);
  if (seconds === null) return { ok: false, reason: 'not_mp4' };
  if (seconds > VIDEO_MAX_SECONDS + 1) return { ok: false, reason: 'too_long' };
  let hasSound = false;
  for (const box of walk(bytes, 0, bytes.length)) {
    // Only the media's own handler (inside mdia) names a sound track.
    if (box.type === 'hdlr' && box.depth >= 2 && isSoundHandler(bytes, box)) hasSound = true;
  }
  return { ok: true, seconds, hasSound };
}
